<template>
  <div class="hero-card" :class="{ 'courses-gate': coursesGate, shortened, promo: course.promo, 'promo-with-timer': course.promoTimer && !shortened }">
    <div v-if="isLms">
      <img :src="`${$config.imagesUrl}/courses/${course.smallImage}`">
      <button v-if="courseLocked" class="btn-danger-transparent">
        <SubtractIcon /> Required XP level: {{ lockedStateBtnCopy }}
        <Tooltip v-if="$config.project === 'lms'">
          Reach XP level {{ lockedStateBtnCopy }} to unlock this course
        </Tooltip>
      </button>
      <div
        v-else-if="course.simpleLmsFree && (($store.getters['auth/user'].simpleLms && !course.purchased) || $config.project === 'site')" :class="{ disabled: previewMode }" class="btn btn-primary"
        @click="$store.dispatch('courses/enrollNow', { courseId: course.id, courseSlug: course.slug })"
      >
        Enroll Now
      </div>
      <nuxt-link
        v-else :to="`/courses/${course.slug}/`" class="btn btn-primary"
        :class="{ disabled: previewMode }"
      >
        {{ course.progress ? 'Continue learning' : 'Start learning' }}
      </nuxt-link>
      <div v-if="$config.project === 'lms'" class="course-content">
        <p class="body-1">
          <CourseContentIcon /> Course content
        </p>
        <ul>
          <li v-for="(item, i) in courseContent" :key="i">
            {{ item.value }} {{ item.text }}
          </li>
        </ul>
      </div>
    </div>
    <template v-else>
      <div v-if="(course.promoTimer || course.promo?.timer) && course.status !== 'locked' && !course.campaignFree" class="limited-time-only-banner">
        Limited-Time Offer
      </div>
      <span v-if="course.promo && course.status !== 'locked' && !coursesGate && !course.campaignFree" class="percents-off">{{ percentOff }}% off</span>
      <img
        v-if="course.campaignFree || course.simpleLmsFree || course.status === 'locked' || (coursesGate && !course.campaignFree && !course.simpleLmsFree)"
        :src="`${$config.imagesUrl}/courses/${course.smallImage}`"
      >
      <div v-if="!(course.simpleLmsFree || course.status === 'locked' || course.campaignFree) || (coursesGate && !course.campaignFree && !course.simpleLmsFree)" class="prices">
        <span v-if="course.promo && course.status !== 'locked' && coursesGate" class="percents-off">{{ percentOff }}% off</span>
        <p v-if="course.promo" class="old-price h4">
          {{ course.currency?.sign }}{{ course.originalPrice }}
        </p>
        <p class="h1 new-price">
          {{ course.price | formatCurrency(course.currency) }}
        </p>
        <p class="caption">
          Lifetime access
        </p>
      </div>
      <Countdown
        v-if="coursesGate && course.promo && course.promo.timer"
        class="promo-counter" divider :end-date="new Date(course.promo.endDate)"
        v-on="$listeners"
      />
      <nuxt-link
        v-if="$config.project === 'site' && course.simpleLmsFree && course.status !== 'locked' && !course.promoEndDate"
        :to="`/signup/?course=${course.slug}`" class="btn btn-primary btn-lg"
      >
        Enroll Now
      </nuxt-link>
      <div v-else-if="course.status === 'locked'" class="warning-alert">
        <WarningIcon />
        <p>
          Please note that this course is available only to
          <LogoGradient :generate-id="id" /> <b>subscribers</b> who have <span v-html="lockedStateCopy" />
        </p>
      </div>
      <template v-else>
        <Countdown
          v-if="course.promoTimer" divider :end-date="new Date(course.promoEndDate)"
          v-on="$listeners"
        />
        <a v-if="course.campaignFree" href="/signup/" class="btn btn-lg btn-primary">
          Start for free
        </a>
        <a
          v-else
          :href="`${$config.siteUrl}/checkout/?plan=${course.productHandle}&ptype=individual-course${promoCode ? `&coupon=${promoCode}` : ''}`"
          class="btn"
          :class="[{ disabled: previewMode }, !course.promo ? 'btn-primary btn-lg' : 'btn-primary-yellow btn-lg']"
        >
          {{ !course.promo ? 'Buy now' : `Get ${percentOff}% OFF` }}
        </a>
        <div v-if="!course.campaignFree" class="money-back-guarantee">
          <GuaranteeIcon />14-Day Money-Back Guarantee
          <GuaranteeIcon />
        </div>
      </template>
      <div v-if="!shortened" class="what-you-get">
        <p class="body-1">
          What you get:
        </p>
        <ul ref="list" :class="{ 'has-scroll': hasScroll, 'no-shadow-end': endScrolled }" @scroll="handleScroll">
          <li v-for="(item, i) in whatYouGetOptions" :key="i">
            <component :is="require(`../../assets/svg/courses/${item.icon}.svg?vue-component`)" :class="item.icon" />
            {{ item.value }} {{ item.text }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
<script>
import GuaranteeIcon from '../../assets/svg/money-back-guarantee.svg?vue-component';
import Countdown from '../../Countdown';
import WarningIcon from '../../assets/svg/icon-warning-gray.svg?vue-component';
import LogoGradient from '../LogoPremiumGradient';
import CourseContentIcon from '../../assets/svg/icon-course-content.svg?vue-component';
import SubtractIcon from '../../assets/svg/icon-locked.svg?vue-component';
import Tooltip from '../../components/Tooltip';

export default {
  components: {
    GuaranteeIcon, Countdown, WarningIcon, LogoGradient, CourseContentIcon, SubtractIcon, Tooltip,
  },
  props: {
    shortened: { type: Boolean },
    coursesGate: { type: Boolean },
    course: { type: Object, required: true, default: () => ({}) },
  },
  data() {
    return {
      hasScroll: false,
      endScrolled: false,
      whatYouGetOptions: [
        this.course.totalHours && { icon: 'hours-of-content', text: `hour${this.course.totalHours === 1 ? '' : 's'} of content`, value: this.course.totalHours },
        this.course.stats.exercises && { icon: 'interactive-exercises', text: `Interactive exercise${this.course.stats.exercises === 1 ? '' : 's'}`, value: this.course.stats.exercises },
        this.course.stats.codingExercises && { icon: 'coding-exercises', text: `Coding exercise${this.course.stats.codingExercises === 1 ? '' : 's'}`, value: this.course.stats.codingExercises },
        this.course.stats.downloadableResources && { icon: 'downloadable-resources', text: `Downloadable resource${this.course.stats.downloadableResources === 1 ? '' : 's'}`, value: this.course.stats.downloadableResources },
        { icon: 'world-class-instructor', text: 'World-class instructor' },
        { icon: 'closed-captions', text: 'Closed captions' },
        { icon: 'q-and-a-support', text: 'Q&A support' },
        { icon: 'future-course-updates', text: 'Future course updates' },
        { icon: 'course-exam', text: 'Course exam' },
        { icon: 'certificate-of-achievement', text: 'Certificate of achievement' },
      ].filter(Boolean),
      courseContent: [
        this.course.totalHours && { text: `hour${this.course.totalHours === 1 ? '' : 's'} of content`, value: this.course.totalHours },
        this.course.stats.exercises && { text: `interactive exercise${this.course.stats.exercises === 1 ? '' : 's'}`, value: this.course.stats.exercises },
        this.course.stats.codingExercises && { text: `coding exercise${this.course.stats.codingExercises === 1 ? '' : 's'}`, value: this.course.stats.codingExercises },
        this.course.stats.downloadableResources && { text: `downloadable resource${this.course.stats.downloadableResources === 1 ? '' : 's'}`, value: this.course.stats.downloadableResources },
        this.course.stats.projects && !this.$store.getters['auth/user'].simpleLms && { text: `real-world data project${this.course.stats.projects === 1 ? '' : 's'}`, value: this.course.stats.projects },
        this.course.stats.practiceExams && { text: `practice exam${this.course.stats.practiceExams === 1 ? '' : 's'}`, value: this.course.stats.practiceExams },
        this.course.status !== 'locked' && { text: 'course exam', value: 1 },
      ].filter(Boolean),
    };
  },
  computed: {
    id() {
      return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
    },
    promoCode() {
      return this.course?.promoCode || this.course?.promo?.code;
    },
    isLms() {
      return this.course.purchased || this.$store.getters['auth/user'].activeSubscription || (this.$config.project === 'lms' && !this.$store.getters['auth/user'].activeSubscription && !this.$store.getters['auth/user'].simpleLms) || (this.$config.project === 'lms' && (this.course.simpleLmsFree));
    },
    percentOff() {
      return Math.round((1 - (this.course.price / this.course.originalPrice)) * 100);
    },
    checkoutUrl() {
      return `${this.$config.siteUrl}/checkout/?plan=${this.course.productHandle}&ptype=individual-course${this.course.promo ? `&coupon=${this.course.promoCode}` : ''}`;
    },
    previewMode() {
      return this.$route.query.preview;
    },
    courseLocked() {
      return this.course.status === 'locked' && !this.course.unlocked;
    },
    lockedStateBtnCopy() {
      const { level } = this.course?.unlockOrigin || {};
      return level;
    },
    lockedStateCopy() {
      const { level, collection, card } = this.course?.unlockOrigin || {};

      if (this.$config.project === 'lms') {
        return `level: ${level}`;
      }

      const copy = [];

      if (level) {
        copy.push(`reached <b>level ${level}</b>`);
      }

      if (collection) {
        copy.push(`unlocked the <b>${collection} card collection</b>`);
      }

      if (card) {
        copy.push(`acquired the <b>${card} card</b>`);
      }

      return copy.join(' or ');
    },
  },
  mounted() {
    this.checkScroll();
    window.addEventListener('resize', this.checkScroll);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScroll);
  },
  methods: {
    handleScroll(event) {
      const { scrollTop, scrollHeight, offsetHeight } = event.target;
      const contentHeight = scrollHeight - offsetHeight;

      if (contentHeight <= scrollTop + 10) {
        this.endScrolled = true;
      } else {
        this.endScrolled = false;
      }

      this.checkScroll();
    },
    checkScroll() {
      const scrollableList = this.$refs.list;
      this.hasScroll = scrollableList?.scrollHeight > scrollableList?.offsetHeight;
    },
  },
};
</script>
<style lang="scss" scoped>
.hero-card {
  transition: ease all var(--duration);
  border-radius: 20px;
  background: var(--main-light);
  box-shadow: 0 4px 15px 0 rgba(0, 12, 31, 0.25);
  padding: var(--spacing-32);
  color: var(--main-dark);
  top: 0;
  position: absolute;
  z-index: 4;
  right: calc(var(--gutter) / 2);
  left: calc(var(--gutter) / 2);

  .limited-time-only-banner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: 12px 8px;
    color: var(--main-light);
    background: var(--main-red);
    font-size: 14px;
    font-weight: 600;
    line-height: 130%;
    text-transform: uppercase;
    text-align: center;
    border-top-right-radius: 20px;
    border-top-left-radius: 20px;
  }

  .percents-off {
    position: absolute;
    right: 12px;
    top: 12px;
    width: 72px;
    height: 72px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 100px;
    border: 3px solid var(--main-red);
    background: var(--main-red-light-3);
    text-align: center;
    font-weight: 600;
    line-height: 114%;
    text-transform: uppercase;
  }

  .prices {
    margin-bottom: var(--spacing-20);

    * {
      margin: 0;
    }

    .old-price {
      font-weight: 500;
      line-height: 110%;
      text-transform: capitalize;
      position: relative;
      display: inline-block;

      &:after {
        content: '';
        position: absolute;
        width: 104%;
        left: -2%;
        right: -2%;
        height: 2px;
        display: block;
        background-color: var(--main-dark);
        top: 50%;
        margin-top: -1px;
      }
    }

    .new-price {
      font-size: 50px;
    }

    .caption {
      color: var(--main-dark-light-4);
    }
  }

  .warning-alert {
    border-radius: 4px;
    background: var(--main-light-shade-3);
    display: flex;
    padding: 8px;
    align-items: flex-start;
    gap: 8px;

    >svg {
      flex: 1 0 auto;
    }

    p {
      font-size: 12px;
      line-height: 130%;
      margin: 0;
    }
  }

  .btn {
    width: 100%;
    color: var(--main-dark);

    &.btn-primary {
      color: var(--main-light);
    }

    &.disabled {
      pointer-events: none;
      opacity: 1;
    }
  }

  ::v-deep .countdown {
    width: 100%;
    margin-bottom: var(--spacing-20);
    color: var(--main-red);

    .box {
      width: 59px;
      height: 49px;

      .number {
        text-align: center;
        font-size: 28px;
        font-weight: 500;
        line-height: 110%;
        padding: 0;
      }
    }
  }

  .money-back-guarantee {
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    margin: var(--spacing-12) 0 0;
    color: var(--main-dark-light-4);
    font-size: 14px;
  }

  .course-content {
    margin-top: var(--spacing-24);

    .body-1 {
      font-weight: 600;
      display: flex;
      align-items: center;
      margin-bottom: var(--spacing-20);
      gap: 12px;
    }

    ul {
      padding: 0;
      margin: 0;

      li {
        margin-bottom: var(--spacing-16);
        padding-left: var(--spacing-8);

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  .what-you-get {
    margin-top: var(--spacing-20);

    .body-1 {
      font-weight: 500;
      margin: 0 0 var(--spacing-12);
    }

    ul {
      max-height: calc(100vh - 460px);
      overflow: auto;
      padding: 0;
      margin: 0;
      list-style: none;

      &.has-scroll {
        &:before {
          content: '';
          background: linear-gradient(0deg, var(--true-white) 24%, rgba(255, 255, 255, 0) 100%);
          position: absolute;
          bottom: 20px;
          left: 0;
          width: 100%;
          height: 150px;
        }

        &.no-shadow-end:before {
          display: none;
        }
      }

      @-moz-document url-prefix() {
        scrollbar-color: var(--main-light-shade-4);
        scrollbar-width: thin;
      }

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: var(--main-light);
      }

      &::-webkit-scrollbar-thumb {
        background: var(--main-light-shade-4);
        border-radius: 50px;
      }

      li {
        display: flex;
        align-items: center;
        margin-bottom: var(--spacing-12);
        line-height: 150%;

        svg {
          margin-right: var(--spacing-12);

          *[stroke] {
            stroke: var(--accent-shade-2);
          }

          &:not(.q-and-a-support) *[fill] {
            fill: var(--accent-shade-2);
          }

          &.coding-exercises {
            width: 15px;
            height: 15px;
          }
        }

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  .btn-danger-transparent {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    position: relative;
    cursor: default;

    svg {
      width: 16px;

      g[stroke] {
        stroke: var(--main-red);
      }
    }

    &:hover {
      background: var(--main-light);
      color: var(--main-red);

      .tooltip {
        display: block;
      }
    }
  }

  img {
    margin-bottom: var(--spacing-20);
    max-width: 100%;
    width: 100%;
    height: 171px;
    object-fit: cover;
    object-position: center;
    border-radius: 10px;
  }

  &.shortened:not(.courses-gate) {
    background: unset;
    box-shadow: none;
    border: 0;
    position: unset;
    padding: 0;
    width: 100%;

    .prices {
      .new-price {
        font-size: 50px;
      }

      img {
        display: none;
      }

      .caption {
        font-size: 16px;
      }
    }

    img,
    .limited-time-only-banner {
      display: none;
    }

    .percents-off {
      top: 0;
    }

    .btn {
      height: 52px;
      padding: var(--spacing-16);
    }
  }

  &.courses-gate {
    background: unset;
    box-shadow: none;
    border: 0;
    position: unset;
    padding: 0;
    width: 100%;

    .prices {
      position: relative;
    }

    .percents-off {
      right: 0;
      top: 0;
    }

    ::v-deep .countdown .box {
      width: 75px;
      height: 49px;
    }

    &.shortened.promo {
      padding-top: 42px;

      img {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
  }

  &.promo-with-timer {
    padding-top: 66px;

    .percents-off {
      top: 54px;
    }
  }

  &.desktop {
    display: block;
  }
}

@include bp($bp-laptop) {
  .hero-card img {
    height: 138px;
  }
}

@include bp($bp-mobile) {
  .hero-card.desktop {
    display: none;
  }

  .hero-card.mobile {
    display: block !important;
    position: relative;
    margin-top: 36px;
    width: calc(100% - 30px);
    box-shadow: none;
    margin-left: -15px;
    margin-right: 15px;
    padding: var(--spacing-48) var(--spacing-24) 0;

    .what-you-get {
      ul {
        max-height: unset;
        overflow: unset;
      }
    }

    .prices {
      .old-price {
        font-size: 24px;
      }

      .new-price {
        font-size: 44px;
      }
    }
  }

  .hero-card {
    .percents-off {
      top: unset;
      right: 24px;
    }

    &.promo-with-timer {
      .prices {
        margin-top: 68px;
      }

      .limited-time-only-banner {
        top: unset;
        border-radius: 20px;
        left: 24px;
        right: 24px;
      }

      .percents-off {
        top: 116px;
        right: 24px;
      }
    }
  }

  .hero-card.shortened:not(.courses-gate) .btn {
    margin-top: var(--spacing-20);
  }

  .hero-card.courses-gate {
    img {
      display: none;
    }

    &.shortened.promo {
      padding-top: 0;
    }
  }
}
</style>
